// ============================================================================
// geometry/barbell.js — code-generated fixed (pre-loaded) barbell (Phase F, product 4)
// ============================================================================
// Ryan's spec (2026-07-15): "The heads are identical to the dumbbells we've
// already designed in every way, shape and form. We have urethane and rubber
// options, both identical. What's different is the handle. The ends of the
// handles where they meet the heads is also identical, but the length is
// different."
//
// So this module is deliberately NOT a second geometry engine. A fixed barbell
// IS a dumbbell with a longer handle — same head, same flute/collar transition,
// same end-cap terrace, same material slots. Duplicating ~570 lines of revolve
// math to change ONE number would guarantee the two products drift apart the
// first time a head detail changes. This module is therefore a thin descriptor:
// the barbell-specific CONSTANTS + a delegation to `createDumbbellGeometry`.
//
//   Fix a head detail in dumbbell.js → the barbell inherits it for free.
//
// THE MEASUREMENT CONVENTION (verified against dumbbell.js:170, not assumed):
//   `gripLength` is measured HEAD-INNER-FACE to HEAD-INNER-FACE — the generator
//   sets `zHeadIn = gripLength / 2`, i.e. half the grip length IS the head's
//   inner face plane. That is exactly Ryan's convention ("40 inches from inside
//   head to inside head"), so his number drops straight in with no adjustment.
//   Cross-check: the dumbbell's 0.145 m = 5.71", and its comment reads "~5.7"".
//
// The flute + collar cylinder live INSIDE that span (they run from `zCham` up to
// `zHeadIn`), so the exposed knurled shaft is shorter than `gripLength` — which
// is correct and matches how the dumbbell already reads.
//
// MATERIAL SLOTS are inherited unchanged (0 BrandFace / 1 body / 2 metal /
// 3 knurl), so reveal-3d's material rig binds a barbell with no special-casing.
//
// PURE (no THREE / WebGL) — the THREE factory takes THREE as an argument.
// ============================================================================

import {
  DEFAULT_DUMBBELL_OPTS,
  buildDumbbellMesh,
  createDumbbellGeometry,
  dumbbellHeadDims,
} from './dumbbell.js';

/** Inches → metres. The spec is quoted in inches; the generator works in metres. */
const IN = 0.0254;

/**
 * Handle length by bar kind, HEAD-INNER-FACE to HEAD-INNER-FACE (metres).
 * Ryan 2026-07-15: straight = 40", EZ-curl = 34".
 *
 * `ezCurl` is recorded here so the number isn't lost, but it is NOT wired up:
 * an EZ-curl handle is a curved shaft, which is real new geometry (the one part
 * of the bar that ISN'T shared with the dumbbell). Ryan: "we'll hold off on
 * those until we perfect the straight ones."
 */
export const BARBELL_GRIP_LENGTHS = Object.freeze({
  straight: 40 * IN, // 1.016 m
  ezCurl: 34 * IN, // 0.8636 m — SPEC ONLY, geometry not built yet
});

/**
 * Barbell defaults = the dumbbell's, with the handle stretched to 40" and
 * urethane selected. Every head/collar/flute/end-cap value is inherited by
 * spread, so "identical in every way, shape and form" is enforced by the code
 * rather than by two lists of numbers agreeing.
 */
export const DEFAULT_BARBELL_OPTS = Object.freeze({
  ...DEFAULT_DUMBBELL_OPTS,
  rubber: false, // urethane straight bar first (Ryan); flip for the rubber option
  kind: 'straight', // 'straight' | 'ezCurl' — see BARBELL_KINDS
  gripLength: BARBELL_GRIP_LENGTHS.straight, // overridden per kind by resolveBarbellOpts
});

/** Steel, kg/m³. Used for exactly one thing: the head-size nudge below. */
const STEEL_DENSITY = 7850;

/** Mass (kg) of a solid steel cylinder — πr²Lρ. */
function shaftMass(radius, length) {
  return Math.PI * radius * radius * length * STEEL_DENSITY;
}

/**
 * Resolves [headRadius, headThickness] (metres) for a barbell denomination.
 *
 * A 40" handle holds ~7× the steel of the dumbbell's 5.7" one, so a "50 lb"
 * barbell puts far less weight in its heads than a 50 lb dumbbell does. Reusing
 * the dumbbell chart raw therefore drew every bar's heads too fat. The fix is to
 * look the chart up by the weight the HEADS actually carry: the denomination less
 * the extra handle steel.
 *
 * Deliberately NOT rocket science (Ryan 2026-07-15: "close enough is fine... this
 * is a visual tool"). It ignores the flutes, collars, and the head's own hardware,
 * and it reuses the dumbbell's chart rather than commissioning a second one. It is
 * one subtraction, costs nothing at runtime, and adds no geometry — it just stops
 * the heads reading a size or two too big. The supplier finishes to real spec.
 *
 * `dumbbellHeadDims` already falls back to the nearest charted row, so light bars
 * that fall off the bottom of the chart resolve to the smallest head rather than
 * failing; the clamp only keeps the lookup positive.
 */
export function barbellHeadDims(weight, unit, opts = {}) {
  const p = { ...DEFAULT_BARBELL_OPTS, ...opts };
  const extraSteelKg = shaftMass(p.handleRadius, p.gripLength) - shaftMass(p.handleRadius, DEFAULT_DUMBBELL_OPTS.gripLength);
  const extraSteel = unit === 'lb' ? extraSteelKg * 2.205 : extraSteelKg;
  const headLoad = Math.max(unit === 'lb' ? 5 : 2.5, weight - extraSteel);
  return dumbbellHeadDims(headLoad, unit);
}

/**
 * Resolves a bar KIND ('straight' | 'ezCurl') into full generator options: its handle
 * length, its marking bands, and its bend.
 *
 * Each of those tests `'<key>' in opts` rather than checking for null, on purpose: the
 * dumbbell's defaults for `knurlZones` and `shaftOffset` ARE null (= knurl the whole
 * shaft / stay straight), so a null check couldn't tell "caller said nothing" from
 * "caller explicitly asked for the null behaviour". Passing them explicitly is a
 * deliberate escape hatch — the preview passes `knurlZones: null` when `?grip` is
 * off-spec and the kind's marking runs no longer span the handle.
 */
function resolveBarbellOpts(opts = {}) {
  const kind = opts.kind || DEFAULT_BARBELL_OPTS.kind;
  const spec = BARBELL_KINDS[kind];
  if (!spec) throw new Error(`unknown barbell kind '${kind}' — expected one of ${Object.keys(BARBELL_KINDS).join(', ')}`);
  const p = { ...DEFAULT_BARBELL_OPTS, gripLength: spec.gripLength, ...opts };
  if (!('knurlZones' in opts)) p.knurlZones = barbellKnurlZones(spec.markings, p.gripLength);
  if (!('shaftOffset' in opts)) p.shaftOffset = spec.curved ? ezCurlShaftOffset(p.gripLength, p.handleRadius) : null;
  return p;
}

/**
 * Pure mesh builder (node-testable, no THREE). Same return shape as
 * `buildDumbbellMesh` — positions/normals/uvs/groups/triangleCount/…
 * @param {Partial<typeof DEFAULT_BARBELL_OPTS>} [opts]
 */
export function buildBarbellMesh(opts = {}) {
  return buildDumbbellMesh(resolveBarbellOpts(opts));
}

/**
 * The straight bar's handle MARKINGS, exactly as Ryan specified them (2026-07-15),
 * read left → right along the handle in INCHES, starting at one head's inner face:
 *
 *   4.666 plain · 4.666 KNURL · 0.5 plain · 4.666 KNURL · 11 plain ·
 *   4.666 KNURL · 0.5 plain · 4.666 KNURL · 4.666 plain
 *
 * Ryan: "including the inside hub/flange parts that attach to the [head]" — so the
 * run starts at the head's inner face, and the first 4.666" plain run SWALLOWS the
 * flute + collar cylinder (~0.65"). That is the same datum `gripLength` uses, so
 * the two specs compose without an offset.
 *
 * THE 4.666 IS 4⅔, AND IS STORED AS 14/3 (Ryan's typed value, taken literally, sums
 * to 39.996" — 0.004" short of the handle, which `barbellKnurlZones` rejected). The
 * exact reading is unambiguous: 40" − 11" centre − two 0.5" gaps = 28" across six
 * equal runs = 14/3" each, and 6 × 14/3 + 11 + 1 = 40.000" exactly. So 4.666 is a
 * 3-decimal rounding of 4⅔, not a literal measurement, and the fraction is kept
 * exact here rather than baking a rounding error into every band's position.
 *
 * The runs sum to 40.000" — the handle length — and the layout is symmetric, which
 * `barbellKnurlZones` asserts rather than trusts.
 */
const RUN = 14 / 3; // 4⅔" — Ryan's "4.666"

export const STRAIGHT_BAR_MARKINGS = Object.freeze([
  { inches: RUN, knurled: false }, // includes the flute/collar hardware
  { inches: RUN, knurled: true },
  { inches: 0.5, knurled: false },
  { inches: RUN, knurled: true },
  { inches: 11, knurled: false }, // the smooth centre
  { inches: RUN, knurled: true },
  { inches: 0.5, knurled: false },
  { inches: RUN, knurled: true },
  { inches: RUN, knurled: false },
]);

/**
 * The EZ bar wears the SAME four-band pattern as the straight bar, every run scaled
 * to its shorter handle (Ryan 2026-07-15: "the knurling pattern I gave you before
 * needs to remain fairly close on this new EZ bar too… so they pass the eyeball test
 * in looking similar enough"). Scaling — rather than keeping runs absolute and
 * eating the difference in the centre — is also what a real EZ bar looks like. Against
 * a competitor's published EZ spec sheet (190 mm grips / 250 mm centre / 640 mm span):
 *
 *   centre    ours 9.35"  vs theirs 9.84"   —  5% off
 *   end runs  ours 3.97"  vs theirs 4.40"   — 10% off
 *   grip/side ours 8.36"  vs theirs 7.48"   — 12% off (ours reads a touch more knurled)
 *
 * i.e. the family look is preserved AND it lands in the right ballpark for the product
 * type. Scaled by CHORD (34/40), not arc: the runs are placed by z, so tiling them to
 * the chord makes them span the shaft exactly. The bar carries ~0.5" more steel than
 * that along its arc, which just makes each band a hair longer than nominal — far
 * below anything the eye reads on a visual tool.
 */
export const EZ_CURL_MARKINGS = Object.freeze(
  STRAIGHT_BAR_MARKINGS.map((m) => Object.freeze({ ...m, inches: (m.inches * 34) / 40 }))
);

/**
 * The EZ bend, measured — not imagined.
 *
 * Sampled at 33 even steps of t = -1..1 along the chord; values are the centreline's
 * sideways offset in SHAFT RADII (+ = up). Traced from Ryan's own silhouette
 * (`EZ-Barbell-Handle-Shape-3.webp`, 2026-07-15) — he outlined the bar flange-to-flange
 * in solid black precisely so it could be traced cleanly. Then symmetrised (an EZ bar
 * IS symmetric; any left/right mismatch is trace noise) and pinned to exactly 0 at both
 * ends, which is what lets the heads, collars and flutes stay untouched bodies of
 * revolution.
 *
 * Shape: troughs at t ≈ ±0.63, peaks at t ≈ ±0.31, and a distinct DIP through the
 * centre — ~0.85 shaft-diameters peak-to-peak.
 *
 * SUPERSEDES a trace of a competitor's spec-sheet drawing. That drawing's amplitude was
 * close (0.98 diameters vs 0.92) but its CENTRE was wrong — it read -0.18 radii where
 * the real bar dips ~-0.31, so the middle rendered nearly flat between the peaks instead
 * of a proper W. Ryan spotted it on sight ("shape is off"). Lesson: the marketing
 * drawing was never to scale, and a clean silhouette beats a busy reference with knurl
 * texture and dimension lines confusing the trace.
 *
 * 65 samples, smoothed with a 81px hanning window before resampling. Both numbers are
 * load-bearing, and a first pass got them wrong:
 *   - The raw trace has a one-pixel NOTCH at dead centre (the brush stroke's joint):
 *     it sits flat at -0.25 either side, spikes to -0.417, and pops straight back. A
 *     light k=15 smoothing left it in, a 33-sample table landed a sample right on it,
 *     and the bar rendered with a hard V through the middle — Ryan: "too abrupt and
 *     jagged… a sharper angle change which isn't realistic". So -0.417 was NOISE; the
 *     real valley floor is ~-0.31, which is what this table now carries.
 *   - Smoothing HARDER (k=121+) also kills the notch but flattens the real W with it
 *     (centre -> -0.27, peaks -> +0.88). k=81 is the most that removes the noise while
 *     leaving the measured shape intact.
 * A parabola through the raw centre gives a ~7.2" bend radius, i.e. the valley is
 * genuinely round — this table has to be fine enough to say so.
 *
 * No pure sine fits this (the best, 2 cycles, misses by 0.8 radii against a ~0.9
 * amplitude), hence a measured table.
 */
export const EZ_CURL_PROFILE = Object.freeze([
  +0.0, -0.0073, -0.0223, -0.0341, -0.0507,
  -0.0929, -0.1552, -0.2667, -0.408, -0.5517,
  -0.6896, -0.7784, -0.7974, -0.7363, -0.5914,
  -0.3847, -0.1518, +0.0847, +0.3259, +0.5634,
  +0.7542, +0.8697, +0.9024, +0.8651, +0.7757,
  +0.6372, +0.4737, +0.3029, +0.1455, +0.0018,
  -0.143, -0.2641, -0.3118, -0.2641, -0.143,
  +0.0018, +0.1455, +0.3029, +0.4737, +0.6372,
  +0.7757, +0.8651, +0.9024, +0.8697, +0.7542,
  +0.5634, +0.3259, +0.0847, -0.1518, -0.3847,
  -0.5914, -0.7363, -0.7974, -0.7784, -0.6896,
  -0.5517, -0.408, -0.2667, -0.1552, -0.0929,
  -0.0507, -0.0341, -0.0223, -0.0073, +0.0,
]);

/**
 * Catmull-Rom: a cubic through p1→p2, shaped by the neighbours p0/p3. Passes through
 * every control point (so the measured samples are preserved exactly) while keeping
 * tangents continuous across them.
 */
function catmullRom(p0, p1, p2, p3, t) {
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    0.5 *
    (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  );
}

/**
 * Builds the `shaftOffset` function for an EZ bar: samples EZ_CURL_PROFILE and scales
 * it from shaft-radii into metres.
 *
 * SMOOTH, not linear (Ryan 2026-07-15: the centre "looks too abrupt and jagged, not a
 * smooth curve but a sharper angle change which isn't realistic"). Linear interpolation
 * makes the profile a POLYLINE — every one of the 33 samples is a corner. Most are too
 * shallow to notice, but at a turning point the slope flips sign in one step, so the
 * centre dip rendered as a hard V instead of a rounded valley. Catmull-Rom still passes
 * through every measured sample but gives continuous tangents, so the bar reads as bent
 * steel rather than folded.
 *
 * Endpoints clamp their virtual neighbour to the end value, which keeps the curve flat
 * into the heads instead of overshooting past 0 there.
 *
 * Clamps t to ±1, and the profile is 0 at both ends, so any z beyond the shaft (the
 * flutes, collars and heads) returns 0 — that clamp IS what keeps the rest of the bar
 * a clean body of revolution.
 *
 * @param {number} gripLength inside-head to inside-head, metres (the CHORD)
 * @param {number} handleRadius metres — the profile is expressed in shaft radii
 * @returns {(z:number) => number}
 */
export function ezCurlShaftOffset(gripLength, handleRadius) {
  const half = gripLength / 2;
  const P = EZ_CURL_PROFILE;
  const N = P.length;
  return (z) => {
    const t = Math.max(-1, Math.min(1, z / half));
    const f = ((t + 1) / 2) * (N - 1);
    const i = Math.min(N - 2, Math.floor(f));
    const frac = f - i;
    const p0 = P[Math.max(0, i - 1)];
    const p1 = P[i];
    const p2 = P[i + 1];
    const p3 = P[Math.min(N - 1, i + 2)];
    return catmullRom(p0, p1, p2, p3, frac) * handleRadius;
  };
}

/**
 * The two bar kinds. Ryan 2026-07-15: every EZ bar shares one handle length, every
 * straight bar shares another, and the two differ — so handle length is a property of
 * the KIND, never of the denomination. (Overall length still grows with weight, since
 * the heads thicken; that's the same reason a maker's spec table lists a different
 * total per weight while the bar itself never changes.)
 */
export const BARBELL_KINDS = Object.freeze({
  straight: Object.freeze({ gripLength: BARBELL_GRIP_LENGTHS.straight, markings: STRAIGHT_BAR_MARKINGS, curved: false }),
  ezCurl: Object.freeze({ gripLength: BARBELL_GRIP_LENGTHS.ezCurl, markings: EZ_CURL_MARKINGS, curved: true }),
});

/**
 * Converts a left→right marking run-list into the `knurlZones` [zFrom, zTo] pairs
 * (metres, from the bar's centre) that `buildDumbbellMesh` consumes.
 *
 * Throws if the runs don't sum to the handle length — a marking list that doesn't
 * add up is a spec error, and silently stretching or truncating it would put the
 * bands in the wrong place while still rendering something plausible.
 *
 * NOTE the diamond PITCH needs no help here: `emitKnurlCylinder` maps v to physical
 * distance, so every band is square and continuous with its neighbours regardless of
 * how many bands there are or how long the handle is.
 *
 * @param {ReadonlyArray<{inches:number, knurled:boolean}>} [markings]
 * @param {number} [gripLength] handle length in metres (inside-head to inside-head)
 * @returns {Array<[number, number]>}
 */
export function barbellKnurlZones(markings = STRAIGHT_BAR_MARKINGS, gripLength = BARBELL_GRIP_LENGTHS.straight) {
  const total = markings.reduce((a, m) => a + m.inches, 0) * IN;
  if (Math.abs(total - gripLength) > 1e-4) {
    throw new Error(
      `barbell markings sum to ${(total / IN).toFixed(3)}" but the handle is ${(gripLength / IN).toFixed(3)}" — ` +
        `the runs must span the handle exactly (inside-head to inside-head).`
    );
  }
  const zones = [];
  let z = -gripLength / 2; // start at one head's inner face
  for (const m of markings) {
    const next = z + m.inches * IN;
    if (m.knurled) zones.push([z, next]);
    z = next;
  }
  return zones;
}

/**
 * THREE factory — mirrors `createDumbbellGeometry(THREE, opts)` exactly, so a
 * caller can swap one for the other. Stamps `userData.barbell` alongside the
 * inherited `userData.dumbbell` (the delegate sets that one) so consumers can
 * tell which product they're looking at.
 * @param {Partial<typeof DEFAULT_BARBELL_OPTS>} [opts]
 */
export function createBarbellGeometry(THREE, opts = {}) {
  const params = resolveBarbellOpts(opts);
  const geometry = createDumbbellGeometry(THREE, params);
  geometry.userData.barbell = {
    kind: params.kind,
    curved: typeof params.shaftOffset === 'function',
    gripLength: params.gripLength,
    gripLengthInches: params.gripLength / IN,
    rubber: !!params.rubber,
    knurlBands: params.knurlZones ? params.knurlZones.length : 0,
    triangleCount: geometry.userData.dumbbell.triangleCount,
    length: geometry.userData.dumbbell.length,
  };
  return geometry;
}
